import React from 'react';
import {
  QrCode,
  MapPin,
  CalendarClock,
  Briefcase,
  Music,
  BookOpen,
  GraduationCap,
  Shield,
} from 'lucide-react';

// Demo placeholder content shown on every card preview — real event/guest
// data gets wired in once a template is applied to an event.
const DEMO = {
  eventTitle: 'Business Networking Event 2026',
  tagline: 'Request the honour of your presence.',
  guestName: 'Amit Kumar Sharma',
  guestRole: 'Managing Director',
  date: '25 Sept, 2026',
  time: '11:59 AM - 01:59 PM',
  venue: 'Ashok Nagar 1st Block, Rabindra Sarabar, Kolkata - 700016',
};

const initials = (name) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('');

const LogoBadge = ({ logoUrl, name, size = 40, color }) =>
  logoUrl ? (
    <div
      className="rounded-full overflow-hidden bg-white shrink-0"
      style={{ width: size, height: size }}
    >
      <img src={logoUrl} alt="Event logo" className="w-full h-full object-cover" />
    </div>
  ) : (
    <div
      className="rounded-full flex items-center justify-center text-white font-bold shrink-0"
      style={{ width: size, height: size, background: color, fontSize: size * 0.32 }}
    >
      {initials(name)}
    </div>
  );

const QrBlock = ({ size = 64, light = false }) => (
  <div
    className="rounded flex items-center justify-center shrink-0"
    style={{
      width: size,
      height: size,
      background: light ? 'rgba(255,255,255,0.95)' : '#fff',
    }}
  >
    <QrCode className="w-2/3 h-2/3 text-gray-900" />
  </div>
);

/**
 * Renders a full invitation-card preview for a template. Layout is chosen by
 * category so each category has its own original look (curved dividers,
 * corner ornaments, etc.) — inspired by common invitation-card structure,
 * not copied artwork.
 */
const InvitationCard = ({ template, data = {} }) => {
  const d = { ...DEMO, ...data };
  const { colorFrom, colorTo, name, category, isPremium } = template;
  const grad = `linear-gradient(160deg, ${colorFrom}, ${colorTo})`;

  // A handful of templates carry an explicit named "layout" that gives a
  // category a second, distinct original card design. Falls back to the
  // per-category switch below when a template has no layout set.
  switch (template.layout) {
    case 'corporate-gala':
      return (
        <div
          className="relative w-full h-full flex flex-col overflow-hidden"
          style={{ background: `linear-gradient(165deg, ${colorFrom}, ${colorTo})` }}
        >
          <svg className="absolute inset-0" viewBox="0 0 300 400" preserveAspectRatio="none">
            <polygon points="0,0 130,0 40,400 0,400" fill="rgba(255,255,255,0.06)" />
            <polygon points="130,0 150,0 60,400 40,400" fill="rgba(255,255,255,0.12)" />
          </svg>
          <div className="relative z-10 flex items-start justify-end px-5 pt-5">
            <LogoBadge logoUrl={d.logoUrl} name={name} size={40} color="rgba(255,255,255,0.15)" />
          </div>
          <div className="relative z-10 px-6 mt-4">
            <h3 className="text-white font-extrabold uppercase leading-tight text-2xl tracking-tight">
              {d.eventTitle}
            </h3>
            <p className="text-white/60 text-xs mt-2">{d.tagline}</p>
          </div>
          <div className="relative z-10 mt-auto px-6 pb-5 flex flex-col items-start gap-2">
            <QrBlock size={54} />
            <p className="text-white font-bold text-sm mt-2">{d.date}</p>
            <p className="text-white/70 text-[11px]">{d.time}</p>
            <p className="text-white/50 text-[10px]">{d.venue}</p>
            <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-white/15 w-full">
              <Briefcase className="w-3 h-3 text-white/40" />
              <span className="text-white/40 text-[9px] tracking-wide">Organized By EventPulse</span>
            </div>
          </div>
          {isPremium && <PremiumBadge />}
        </div>
      );

    case 'entertainment-cultural':
      return (
        <div
          className="relative w-full h-full flex flex-col px-5 py-6 overflow-hidden"
          style={{ background: `linear-gradient(180deg, #fffbeb, ${colorFrom}33)` }}
        >
          <div
            className="absolute -top-6 -right-10 w-40 h-40 rounded-full opacity-30"
            style={{ background: colorTo }}
          />
          <div
            className="absolute -bottom-10 -left-8 w-32 h-32 rounded-full opacity-20"
            style={{ background: colorFrom }}
          />
          <div className="relative z-10 flex items-center gap-2">
            <Music className="w-5 h-5" style={{ color: colorTo }} />
            <p className="text-xs text-gray-600">{name} presents</p>
          </div>
          <h3 className="relative z-10 font-extrabold text-gray-900 text-xl leading-tight mt-4">
            {d.eventTitle}
          </h3>
          <p className="relative z-10 text-gray-600 text-xs mt-2">{d.tagline}</p>
          <div className="relative z-10 mt-auto flex items-end justify-between">
            <div>
              <p className="text-gray-900 font-semibold text-sm">{d.date}</p>
              <p className="text-gray-500 text-[10px]">{d.time}</p>
              <p className="text-gray-400 text-[9px] mt-1 max-w-[140px]">{d.venue}</p>
            </div>
            <QrBlock size={50} />
          </div>
          {isPremium && <PremiumBadge />}
        </div>
      );

    case 'academic-conference':
      return (
        <div
          className="relative w-full h-full flex flex-col items-center text-center px-6 py-6 overflow-hidden"
          style={{ background: `linear-gradient(180deg, ${colorFrom}22, ${colorTo}22)` }}
        >
          <div
            className="absolute inset-3 border-2 border-dashed rounded-lg pointer-events-none"
            style={{ borderColor: `${colorTo}55` }}
          />
          <BookOpen className="absolute top-4 left-4 w-4 h-4" style={{ color: colorTo, opacity: 0.5 }} />
          <GraduationCap className="absolute top-4 right-4 w-4 h-4" style={{ color: colorTo, opacity: 0.5 }} />
          <h3 className="relative z-10 font-bold text-gray-900 text-lg mt-4 leading-tight">
            {d.eventTitle}
          </h3>
          <p className="relative z-10 text-gray-600 text-[10px] mt-2 px-2">{d.tagline}</p>
          <div
            className="relative z-10 mt-4 w-16 h-16 rounded-full border-2 flex items-center justify-center"
            style={{ borderColor: colorTo }}
          >
            <Shield className="w-7 h-7" style={{ color: colorTo }} />
          </div>
          <div className="relative z-10 mt-auto flex flex-col items-center gap-2 pb-1">
            <QrBlock size={50} />
            <p className="text-gray-900 text-[11px] font-semibold">{d.date}</p>
            <p className="text-gray-500 text-[10px]">{d.time}</p>
          </div>
          {isPremium && <PremiumBadge />}
        </div>
      );

    case 'social-picnic':
      return (
        <div
          className="relative w-full h-full flex flex-col px-5 py-6 overflow-hidden"
          style={{ background: grad }}
        >
          <LeafCorner color="rgba(255,255,255,0.35)" />
          <LeafCorner color="rgba(255,255,255,0.25)" flip />
          <div className="relative z-10 flex items-center gap-2">
            <LogoBadge logoUrl={d.logoUrl} name={name} size={32} color="rgba(255,255,255,0.25)" />
            <p className="text-white/70 text-[9px] tracking-widest">{d.logoTagline || 'TAG LINE HERE'}</p>
          </div>
          <h3 className="relative z-10 text-white font-extrabold text-xl leading-tight mt-4">
            {d.eventTitle}
          </h3>
          <p className="relative z-10 text-white/80 text-xs mt-2">{d.tagline}</p>
          <div className="relative z-10 mt-auto flex items-end justify-between">
            <div>
              <p className="text-white font-semibold text-sm">{d.date}</p>
              <p className="text-white/70 text-[10px]">{d.time}</p>
            </div>
            <QrBlock size={50} />
          </div>
          {isPremium && <PremiumBadge />}
        </div>
      );

    default:
      break;
  }

  switch (category) {
    case 'Corporate':
      return (
        <div className="relative w-full h-full flex flex-col bg-white overflow-hidden">
          <div className="flex flex-col items-center pt-6 pb-8 px-4 text-center relative z-10">
            <div className="mb-2">
              <LogoBadge logoUrl={d.logoUrl} name={name} size={40} color={colorFrom} />
            </div>
            <p className="text-[9px] tracking-widest text-gray-400 mb-2">{d.logoTagline || 'TEMPLATE'}</p>
            <h3 className="font-bold text-gray-900 leading-tight text-sm">{d.eventTitle}</h3>
            <p className="text-[10px] text-gray-500 mt-1">{d.tagline}</p>
          </div>
          <svg
            className="absolute top-[68px] left-0 w-full"
            viewBox="0 0 300 40"
            preserveAspectRatio="none"
            style={{ height: 40 }}
          >
            <path d="M0,40 Q150,0 300,40 L300,40 L0,40 Z" fill={colorFrom} />
          </svg>
          <div
            className="flex-1 flex flex-col items-center justify-between px-5 pb-4 pt-8 text-center"
            style={{ background: grad }}
          >
            <QrBlock size={56} />
            <div className="text-white">
              <p className="font-semibold text-sm">{d.guestName}</p>
              <p className="text-white/70 text-[10px]">{d.guestRole}</p>
              <p className="text-white text-[11px] font-medium mt-2">{d.date}</p>
              <p className="text-white/80 text-[10px]">{d.time}</p>
              <p className="text-white/60 text-[9px] mt-1 leading-snug">{d.venue}</p>
              {d.organizerLogoUrl !== undefined && (
                <div className="flex flex-col items-center gap-1 mt-3 pt-2 border-t border-white/20">
                  <span className="text-white/50 text-[8px] tracking-wide">Organized By</span>
                  {d.organizerLogoUrl ? (
                    <img src={d.organizerLogoUrl} alt="Organizer logo" className="h-4 object-contain" />
                  ) : (
                    <span className="text-white/70 text-[9px] font-semibold">QRites</span>
                  )}
                </div>
              )}
            </div>
          </div>
          {isPremium && <PremiumBadge />}
        </div>
      );

    case 'Wedding':
      return (
        <div
          className="relative w-full h-full flex flex-col items-center text-center px-5 py-6 overflow-hidden"
          style={{ background: `linear-gradient(180deg, ${colorFrom}22, ${colorTo}33)` }}
        >
          <CornerFlourish color={colorTo} />
          <CornerFlourish color={colorTo} flip />
          <p className="text-[10px] tracking-wide text-gray-500 mt-2">{d.tagline}</p>
          <p
            className="italic mt-2"
            style={{ fontFamily: 'Georgia, serif', color: colorTo, fontSize: 22 }}
          >
            {name}
          </p>
          <h3 className="font-semibold text-gray-800 text-base mt-2">{d.guestName}</h3>
          <div className="w-10 h-px bg-gray-400 my-3" />
          <p className="text-xs font-medium text-gray-700">{d.date}</p>
          <p className="text-[10px] text-gray-500">{d.time}</p>
          <p className="text-[9px] text-gray-400 mt-1 px-4">{d.venue}</p>
          <div className="mt-auto pt-3">
            <QrBlock size={52} />
          </div>
          {isPremium && <PremiumBadge />}
        </div>
      );

    case 'Academic':
      return (
        <div
          className="relative w-full h-full flex flex-col items-center text-center px-5 py-6"
          style={{ background: grad }}
        >
          <div className="w-14 h-14 rounded-full border-2 border-white/70 flex items-center justify-center mt-2 p-1">
            <LogoBadge logoUrl={d.logoUrl} name={name} size={44} color="rgba(255,255,255,0.25)" />
          </div>
          <p className="text-white/70 text-[9px] tracking-widest mt-3">CORDIALLY INVITES YOU TO</p>
          <h3 className="text-white font-bold text-base mt-1 leading-tight">{d.eventTitle}</h3>
          <p className="text-white/70 text-[10px] mt-2">{d.tagline}</p>
          <div className="mt-auto flex flex-col items-center gap-2 pb-1">
            <QrBlock size={54} />
            <p className="text-white text-[11px] font-medium">{d.date}</p>
            <p className="text-white/70 text-[9px]">{d.time}</p>
          </div>
          {isPremium && <PremiumBadge />}
        </div>
      );

    case 'Entertainment':
      return (
        <div
          className="relative w-full h-full flex flex-col text-center px-5 py-6 overflow-hidden"
          style={{ background: grad }}
        >
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, #fff 0, transparent 40%), radial-gradient(circle at 80% 70%, #fff 0, transparent 35%)',
          }} />
          <p className="text-white/60 text-[9px] tracking-widest relative z-10 mt-2">AN EVENING OF</p>
          <h3 className="text-white font-extrabold text-lg relative z-10 leading-tight mt-1">{d.eventTitle}</h3>
          <div className="flex-1 flex flex-col items-center justify-center relative z-10">
            <p className="text-white font-semibold text-sm">{d.guestName}</p>
            <p className="text-white/60 text-[10px]">Special Guest</p>
          </div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="text-left">
              <p className="text-white text-[10px] font-medium">{d.date}</p>
              <p className="text-white/60 text-[9px]">{d.time}</p>
            </div>
            <QrBlock size={44} />
          </div>
          {isPremium && <PremiumBadge />}
        </div>
      );

    case 'Sports':
      return (
        <div className="relative w-full h-full flex flex-col overflow-hidden">
          <div
            className="h-1/3 flex items-end px-4 pb-3"
            style={{ background: `linear-gradient(120deg, ${colorFrom}, ${colorTo})` }}
          >
            <h3 className="text-white font-black text-lg leading-none uppercase">{d.eventTitle}</h3>
          </div>
          <div className="flex-1 bg-gray-900 flex flex-col items-center justify-center text-center px-4 py-4">
            <p className="text-white font-semibold text-sm">{d.guestName}</p>
            <p className="text-gray-400 text-[10px] mb-3">Tournament Partner</p>
            <QrBlock size={50} />
            <p className="text-white text-[10px] font-medium mt-3">{d.date}</p>
            <p className="text-gray-400 text-[9px]">{d.time}</p>
          </div>
          {isPremium && <PremiumBadge />}
        </div>
      );

    case 'Personal':
      return (
        <div
          className="relative w-full h-full flex flex-col items-center text-center px-5 py-6"
          style={{ background: grad }}
        >
          <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center mt-2 border-4 border-white/40">
            <span className="text-lg font-bold" style={{ color: colorTo }}>
              {initials(d.guestName)}
            </span>
          </div>
          <h3 className="text-white font-bold text-lg mt-3">{name}</h3>
          <p className="text-white/80 text-sm mt-1">{d.guestName}</p>
          <div className="mt-auto flex flex-col items-center gap-2 pb-1">
            <QrBlock size={50} />
            <p className="text-white text-[11px] font-medium">{d.date}</p>
            <p className="text-white/70 text-[9px]">{d.time}</p>
          </div>
          {isPremium && <PremiumBadge />}
        </div>
      );

    case 'Social':
    default:
      return (
        <div
          className="relative w-full h-full flex flex-col items-center text-center px-5 py-6 overflow-hidden"
          style={{ background: grad }}
        >
          <ConfettiDots />
          <p className="text-white/70 text-[9px] tracking-widest mt-2 relative z-10">YOU'RE INVITED TO</p>
          <h3 className="text-white font-bold text-base mt-1 relative z-10 leading-tight">{d.eventTitle}</h3>
          <div className="mt-auto flex flex-col items-center gap-2 pb-1 relative z-10">
            <QrBlock size={52} />
            <p className="text-white text-[11px] font-medium">{d.date}</p>
            <p className="text-white/70 text-[9px] px-4">{d.venue}</p>
          </div>
          {isPremium && <PremiumBadge />}
        </div>
      );
  }
};

const PremiumBadge = () => (
  <div className="absolute top-2 right-2 bg-orange-500 text-white text-[9px] font-semibold px-2 py-0.5 rounded-full z-20">
    Premium
  </div>
);

const CornerFlourish = ({ color, flip = false }) => (
  <svg
    className={`absolute w-14 h-14 ${flip ? 'bottom-1 right-1 rotate-180' : 'top-1 left-1'}`}
    viewBox="0 0 60 60"
  >
    <path
      d="M2 2 C 20 2, 2 20, 2 40 M2 2 C 2 20, 20 2, 40 2"
      stroke={color}
      strokeWidth="1.5"
      fill="none"
      opacity="0.5"
    />
    <circle cx="10" cy="10" r="2.5" fill={color} opacity="0.6" />
  </svg>
);

const LeafCorner = ({ color, flip = false }) => (
  <svg
    className={`absolute z-10 w-16 h-16 ${flip ? 'bottom-2 left-2 rotate-180' : 'top-2 right-2'}`}
    viewBox="0 0 60 60"
  >
    <path
      d="M4 30 C 4 14, 20 4, 40 6 C 34 18, 34 30, 44 40 C 26 42, 8 38, 4 30 Z"
      fill={color}
    />
    <path d="M8 28 C 18 26, 28 30, 36 38" stroke="rgba(0,0,0,0.08)" strokeWidth="1.5" fill="none" />
  </svg>
);

const ConfettiDots = () => (
  <div className="absolute inset-0">
    {[...Array(10)].map((_, i) => (
      <span
        key={i}
        className="absolute rounded-full bg-white/40"
        style={{
          width: 4 + (i % 3) * 2,
          height: 4 + (i % 3) * 2,
          left: `${(i * 37) % 100}%`,
          top: `${(i * 53) % 100}%`,
        }}
      />
    ))}
  </div>
);

export default InvitationCard;
